import { RemoteSessionMethod } from '@datacamp/multiplexer-client-jsonrpc';
import { z } from 'zod';
/*
 * Define the methods that can be called on the remote session.
 */
export const Language = z.union([z.literal('java'), z.literal('rust')]);
export type Language = z.infer<typeof Language>;

export const BackendError = z.object({
  error: z.string(),
  type: z.literal('backend-error'),
});
export type BackendError = z.infer<typeof BackendError>;

export const RunCodeParams = z.object({
  code: z.string(),
  language: Language,
});
export type RunCodeParams = z.infer<typeof RunCodeParams>;

export const RunCodeOutput = z.object({
  output: z.string(),
  type: z.union([z.literal('output'), z.literal('error')]),
});
export type RunCodeOutput = z.infer<typeof RunCodeOutput>;
export const RunCodeResult = z.union([RunCodeOutput, BackendError]);
export type RunCodeResult = z.infer<typeof RunCodeResult>;

export const SctResult = z.object({
  correct: z.boolean(),
  message: z.string(),
});
export type SctResult = z.infer<typeof SctResult>;
export const SubmitCodeParams = z.object({
  code: z.string(),
  language: Language,
  sct: z.string(),
});
export type SubmitCodeParams = z.infer<typeof SubmitCodeParams>;

export const SubmitCodeOutput = z.object({
  runResult: RunCodeOutput,
  sctResult: SctResult,
  type: z.literal('output'),
});
export type SubmitCodeOutput = z.infer<typeof SubmitCodeOutput>;
export const SubmitCodeResult = z.union([SubmitCodeOutput, BackendError]);
export type SubmitCodeResult = z.infer<typeof SubmitCodeResult>;

export const Methods = {
  runCode: {
    params: RunCodeParams,
    result: RunCodeResult,
  },
  submitCode: {
    params: SubmitCodeParams,
    result: SubmitCodeResult,
  },
};

export type Methods = {
  [MethodName in keyof typeof Methods]: RemoteSessionMethod<
    z.TypeOf<typeof Methods[MethodName]['params']>,
    z.TypeOf<typeof Methods[MethodName]['result']>
  >;
};

export const RemoteNotifications = {};
export type RemoteNotifications = {};
/*
 * Define the response decoders for the remote session.
 */
export type ResponseDecoders = {
  [MethodName in keyof typeof Methods]: (
    output: unknown,
  ) => z.infer<typeof Methods[MethodName]['result']>;
};

export function decodeOrReturnOriginal(schema: z.ZodSchema) {
  const decoder = (value: unknown) => {
    const result = schema.safeParse(value);
    if (!result.success) {
      return value;
    }
    return result.data;
  };

  return decoder;
}

export const responseDecoders: ResponseDecoders = {
  runCode: decodeOrReturnOriginal(Methods.runCode.result),
  submitCode: decodeOrReturnOriginal(Methods.submitCode.result),
};

export type NotificationDecoders = {};
export const notificationDecoders: NotificationDecoders = {};

/*
 * Augment the RemoteSession and RemoteNotificationHandlers interfaces
 */
declare module '@datacamp/multiplexer-client-jsonrpc' {
  // eslint-disable-next-line prefer-type-alias/prefer-type-alias, @typescript-eslint/no-empty-interface
  interface RemoteSession extends Methods {}

  // eslint-disable-next-line prefer-type-alias/prefer-type-alias, @typescript-eslint/no-empty-interface
  interface RemoteNotificationHandlers extends RemoteNotifications {}
}
